import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    computed,
    effect,
    inject,
    signal,
    untracked,
} from '@angular/core';
import {outputToObservable, takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {NonNullableFormBuilder} from '@angular/forms';
import {
    BehaviorSubject,
    catchError,
    EMPTY,
    expand,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    tap,
    timer,
} from 'rxjs';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {
    FeatureFlagService,
    TranslatePipe,
    TranslateService,
    buildDuplicateName,
    caseInsensitiveComparator,
    keyComparator,
    toggleInSet,
} from '@brunata-angular/util';
import {
    ConfirmationDialogService,
    LoadComponent,
    ModalInputService,
    SelectableItem,
    Sorting,
} from '@brunata-angular/ui';
import {escape as escapeHtml} from 'lodash-es';
import {ToastrService} from 'ngx-toastr';
import {injectHoldsAdvancedDelivery} from '../shared/services/advanced-delivery';
import {SavedExportsService} from './saved-exports.service';
import {
    SavedExport,
    SavedExportCreateRequest,
    hasMeterScope,
    toSavedExportCreateRequest,
} from './saved-export.model';
import {
    SavedExportFilterForm,
    SavedExportFilterOptions,
    SavedExportFilterValues,
    deselectAll,
    emptySavedExportFilterOptions,
    emptySavedExportFilterValues,
    matchesSavedExportFilters,
    savedExportFilterIds,
    savedExportFilterOptions,
    selectedOptionIds,
    toSelectableItems,
} from './saved-exports-filters.model';
import {SavedExportsTableComponent} from './saved-exports-table/saved-exports-table.component';
import {SavedExportCardComponent} from './saved-export-card/saved-export-card.component';
import {ExportNowModalComponent} from './export-now-modal/export-now-modal.component';
import {EditSavedExportModalComponent} from './edit-saved-export-modal/edit-saved-export-modal.component';
import {EditCustomizedExportModalComponent} from './edit-customized-export-modal/edit-customized-export-modal.component';
import {
    DeliveryLogModalComponent,
    DeliveryLogModalInput,
} from './delivery-log-modal/delivery-log-modal.component';

type SavedExportsView = {
    savedExports: SavedExport[];
    totalCount: number;
};

type AcceptedPresses = {
    acceptedOn: SavedExport[] | null;
    savedExportIds: ReadonlySet<string>;
};

const defaultSorting: Sorting = {column: 'name', direction: 'asc'};

const VIEW_MODE_STORAGE_KEY = 'saved-exports.overview.view-mode';

const DELIVERY_REQUESTED_RELOAD_MS = 30_000;

@Component({
    selector: 'app-saved-exports',
    standalone: true,
    imports: [TranslatePipe, SavedExportsTableComponent, SavedExportCardComponent, LoadComponent],
    templateUrl: './saved-exports.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SavedExportsComponent {
    private readonly savedExportsService = inject(SavedExportsService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly toastrService = inject(ToastrService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(NgbModal);
    private readonly modalInputService = inject(ModalInputService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly holdsAdvancedDelivery = injectHoldsAdvancedDelivery();

    protected readonly sorting = signal<Sorting>(defaultSorting);
    protected readonly exportingIds = signal<ReadonlySet<string>>(new Set());
    private readonly pressesInFlight = signal<ReadonlySet<string>>(new Set());
    private readonly acceptedPresses = signal<AcceptedPresses>({
        acceptedOn: null,
        savedExportIds: new Set(),
    });

    protected readonly deliveryLogEnabled = toSignal(
        this.featureFlagService.isFeatureEnabled$('agi-18212-online-delivery-log'),
        {initialValue: false},
    );

    protected readonly alignedDesign = toSignal(
        this.featureFlagService.isFeatureEnabled$('agi-20470-online-saved-exports-design'),
        {initialValue: false},
    );

    protected readonly longNameLayout = toSignal(
        this.featureFlagService.isFeatureEnabled$('agi-20785-online-export-long-names'),
        {initialValue: false},
    );

    protected readonly viewMode = signal<'card' | 'table'>(
        localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'card' ? 'card' : 'table',
    );

    protected readonly filterForm: SavedExportFilterForm = this.formBuilder.group({
        searchTerm: '',
        outputFormats: this.formBuilder.control<SelectableItem[]>([]),
        schedules: this.formBuilder.control<SelectableItem[]>([]),
    });

    private readonly filterOptions = signal<SavedExportFilterOptions>(
        emptySavedExportFilterOptions,
    );

    private readonly outputFormatItems = toSignal(
        this.filterForm.controls.outputFormats.valueChanges,
        {initialValue: this.filterForm.controls.outputFormats.value},
    );

    private readonly scheduleItems = toSignal(this.filterForm.controls.schedules.valueChanges, {
        initialValue: this.filterForm.controls.schedules.value,
    });

    private readonly selectedIds = computed(() => ({
        outputFormats: selectedOptionIds(
            this.filterOptions().outputFormats,
            this.outputFormatItems(),
        ),
        schedules: selectedOptionIds(this.filterOptions().schedules, this.scheduleItems()),
    }));

    private readonly reload$ = new BehaviorSubject<void>(undefined);

    private readonly savedExports$: Observable<SavedExport[]> = this.reload$.pipe(
        switchMap(() =>
            this.loadSavedExports([]).pipe(
                expand(savedExports =>
                    savedExports.some(savedExport => savedExport.deliveryRequested === true)
                        ? timer(DELIVERY_REQUESTED_RELOAD_MS).pipe(
                              switchMap(() => this.loadSavedExports(savedExports)),
                          )
                        : EMPTY,
                ),
            ),
        ),
        takeUntilDestroyed(this.destroyRef),
        shareReplay(1),
    );

    private readonly savedExports = toSignal(this.savedExports$, {initialValue: null});

    protected readonly deliveryRequestedIds = computed<ReadonlySet<string>>(() => {
        const savedExports = this.savedExports();
        const accepted = this.acceptedPresses();
        const requestedOnServer = (savedExports ?? [])
            .filter(savedExport => savedExport.deliveryRequested === true)
            .map(savedExport => savedExport.savedExportId);
        const acceptedOnThisList =
            accepted.acceptedOn === savedExports ? [...accepted.savedExportIds] : [];
        return new Set([...requestedOnServer, ...this.pressesInFlight(), ...acceptedOnThisList]);
    });

    private readonly searchTerm = toSignal(this.filterForm.controls.searchTerm.valueChanges, {
        initialValue: this.filterForm.controls.searchTerm.value,
    });

    private readonly filters = computed<SavedExportFilterValues>(() => ({
        searchTerm: this.searchTerm(),
        ...this.selectedIds(),
    }));

    protected readonly view = computed<SavedExportsView | null>(() => {
        const savedExports = this.savedExports();
        if (savedExports === null) {
            return null;
        }
        const isTable = this.viewMode() === 'table';
        const filters = isTable ? this.filters() : emptySavedExportFilterValues;
        const matching = savedExports.filter(savedExport =>
            matchesSavedExportFilters(savedExport, savedExportFilterIds(savedExport), filters),
        );
        return {
            savedExports: isTable ? this.sortSavedExports(matching, this.sorting()) : matching,
            totalCount: savedExports.length,
        };
    });

    constructor() {
        effect(() => {
            const savedExports = this.savedExports();
            if (savedExports !== null) {
                this.offerFilterOptions(savedExports);
            }
        });
    }

    setViewMode(view: 'card' | 'table'): void {
        this.viewMode.set(view);
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, view);
    }

    onSort(sorting: Sorting): void {
        this.sorting.set(sorting);
    }

    resetFiltersAndSorting(): void {
        this.filterForm.controls.searchTerm.reset('');
        this.clearColumnFilters();
        this.sorting.set(defaultSorting);
    }

    private loadSavedExports(keptOnFailure: SavedExport[]): Observable<SavedExport[]> {
        return this.savedExportsService.getSavedExports().pipe(
            map(page => page.savedExports),
            catchError(() => of(keptOnFailure)),
        );
    }

    private offerFilterOptions(savedExports: SavedExport[]): void {
        const options = savedExportFilterOptions(savedExports, labelKey =>
            this.scheduleLabel(labelKey),
        );
        const selected = untracked(this.selectedIds);

        this.filterOptions.set(options);
        this.filterForm.patchValue({
            outputFormats: toSelectableItems(options.outputFormats, selected.outputFormats),
            schedules: toSelectableItems(options.schedules, selected.schedules),
        });
    }

    private clearColumnFilters(): void {
        this.filterForm.patchValue({
            outputFormats: deselectAll(this.filterForm.controls.outputFormats.value),
            schedules: deselectAll(this.filterForm.controls.schedules.value),
        });
    }

    private scheduleLabel(scheduleLabelKey: string): string {
        return this.translateService.translate(scheduleLabelKey, 'online', 'export');
    }

    private sortSavedExports(savedExports: SavedExport[], sorting: Sorting): SavedExport[] {
        const comparator: (a: SavedExport, b: SavedExport) => number =
            sorting.column === 'startDate'
                ? keyComparator(savedExport => savedExport.startDate, sorting.direction, 'last')
                : caseInsensitiveComparator(savedExport => savedExport.name, sorting.direction);

        return [...savedExports].sort(comparator);
    }

    editSavedExport(savedExport: SavedExport): void {
        const isCustomized =
            (savedExport.exportType === 'CUSTOMIZED' || hasMeterScope(savedExport)) &&
            this.featureFlagService.isFeatureEnabled('online-customized-exports');
        const modalRef = this.modalService.open(
            isCustomized ? EditCustomizedExportModalComponent : EditSavedExportModalComponent,
            {
                windowClass: 'modal-extra-wide',
                backdrop: 'static',
                keyboard: false,
                centered: true,
                scrollable: true,
            },
        );
        const instance: unknown = modalRef.componentInstance;
        if (
            !(instance instanceof EditSavedExportModalComponent) &&
            !(instance instanceof EditCustomizedExportModalComponent)
        ) {
            modalRef.dismiss();
            return;
        }
        instance.savedExport.set(savedExport);
        outputToObservable(instance.save)
            .pipe(
                switchMap(payload => {
                    instance.isSaving.set(true);
                    return this.savedExportsService
                        .updateSavedExport(savedExport.savedExportId, payload)
                        .pipe(
                            // HTTP interceptor surfaces the generic error toast; swallow here so the save
                            // stream survives and the modal stays open for a retry.
                            catchError(() => EMPTY),
                            finalize(() => instance.isSaving.set(false)),
                        );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                modalRef.close();
                this.reload$.next();
                this.toastrService.success(
                    this.translateService.translate('savedexport_edit_success', 'online', 'export'),
                );
            });
    }

    duplicateSavedExport(savedExport: SavedExport): void {
        if (this.refuseSftpWithoutAdvancedDelivery(savedExport)) {
            return;
        }
        const request: SavedExportCreateRequest = {
            ...toSavedExportCreateRequest(savedExport),
            name: buildDuplicateName(savedExport.name, this.translateService),
        };
        this.savedExportsService.createSavedExport(request).subscribe(created => {
            this.reload$.next();
            this.toastrService.success(
                this.translateService.translate('savedexport_create_success', 'online', 'export'),
            );

            this.editSavedExport({...created, outputFormat: savedExport.outputFormat});
        });
    }

    private refuseSftpWithoutAdvancedDelivery(savedExport: SavedExport): boolean {
        if (savedExport.deliveryChannel !== 'SFTP' || this.holdsAdvancedDelivery() === true) {
            return false;
        }
        this.toastrService.error(
            this.translateService.translate(
                'savedexport_sftp_requires_advanced_delivery',
                'online',
                'export',
            ),
        );
        return true;
    }

    // A null saved export opens the log across every export, which is the page's own button.
    openDeliveryLog(savedExport: SavedExport | null): void {
        const input: DeliveryLogModalInput = {savedExport};
        this.modalInputService.open(DeliveryLogModalComponent, input, {
            size: 'xl',
            scrollable: true,
            centered: true,
        });
    }

    exportSavedExport(savedExport: SavedExport): void {
        if (this.refuseSftpWithoutAdvancedDelivery(savedExport)) {
            return;
        }
        if (savedExport.recurrence !== null) {
            if (this.featureFlagService.isFeatureEnabled('agi-20036-export-now-queue')) {
                this.requestDelivery(savedExport.savedExportId);
                return;
            }
            this.exportingIds.update(ids => new Set(ids).add(savedExport.savedExportId));
            this.savedExportsService
                .exportSavedExportNow(savedExport.savedExportId)
                .pipe(
                    catchError(() => EMPTY),
                    finalize(() => this.stopExporting(savedExport.savedExportId)),
                )
                .subscribe(() => {
                    this.toastrService.success(
                        this.translateService.translate(
                            'savedexport_export_now_success',
                            'online',
                            'export',
                        ),
                    );
                });
            return;
        }

        const modalRef = this.modalService.open(ExportNowModalComponent, {centered: true});
        const modal: unknown = modalRef.componentInstance;
        if (modal instanceof ExportNowModalComponent === false) {
            return;
        }
        modal.savedExport.set(savedExport);
        modal.export.subscribe(({readingDate, timeZone}) => {
            modal.isExporting.set(true);
            this.savedExportsService
                .downloadSavedExport(savedExport, readingDate, timeZone)
                .pipe(finalize(() => modal.isExporting.set(false)))
                .subscribe({
                    next: () => modalRef.close(),
                    error: () => {
                        // HTTP interceptor surfaces the generic error toast
                    },
                });
        });
    }

    private stopExporting(savedExportId: string): void {
        this.exportingIds.update(ids => {
            const remaining = new Set(ids);
            remaining.delete(savedExportId);
            return remaining;
        });
    }

    private requestDelivery(savedExportId: string): void {
        this.pressesInFlight.update(ids => toggleInSet(ids, savedExportId));
        this.savedExportsService
            .exportSavedExportNow(savedExportId)
            .pipe(
                tap(() => this.recordAcceptedPress(savedExportId)),
                catchError(() => EMPTY),
                finalize(() => {
                    this.pressesInFlight.update(ids => toggleInSet(ids, savedExportId));
                    this.reload$.next();
                }),
            )
            .subscribe(() => {
                this.toastrService.success(
                    this.translateService.translate(
                        'savedexport_export_now_requested',
                        'online',
                        'export',
                    ),
                );
            });
    }

    private recordAcceptedPress(savedExportId: string): void {
        const savedExports = this.savedExports();
        this.acceptedPresses.update(accepted => ({
            acceptedOn: savedExports,
            savedExportIds: new Set([
                ...(accepted.acceptedOn === savedExports ? accepted.savedExportIds : []),
                savedExportId,
            ]),
        }));
    }

    deleteSavedExport(savedExport: SavedExport): void {
        void this.confirmationDialogService
            .confirmWithSettings({
                title: this.translateService.translate('common_warning', 'online', 'common'),
                message: this.translateService
                    .translate('savedexport_delete_warning', 'online', 'export')
                    .replace('{0}', () => escapeHtml(savedExport.name)),
                centered: true,
            })
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.savedExportsService
                    .deleteSavedExport(savedExport.savedExportId)
                    .subscribe(() => {
                        this.reload$.next();
                        this.toastrService.success(
                            this.translateService.translate(
                                'savedexport_delete_success',
                                'online',
                                'export',
                            ),
                        );
                    });
            });
    }
}
